'use client'

import { Box, Collapse, Flex, Text, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import ChevronDown from '@/assets/icons/chevron-down.svg'
import ChevronUp from '@/assets/icons/chevron-up.svg'
import InfoCircle from '@/assets/icons/info-circle.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'

interface Definition {
  title: string
  description: string
}

interface DefinitionRowProps extends Definition {
  isLast: boolean
}

function DefinitionRow({ title, description, isLast }: DefinitionRowProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box>
      <UnstyledButton onClick={() => setExpanded(v => !v)} style={{ width: '100%' }}>
        <Flex align="center" gap={8} px={24} py={12} style={{ minHeight: 52 }}>
          <Text
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              color: 'var(--content-primary)',
            }}
          >
            {title}
          </Text>
          {expanded ? (
            <ChevronUp width={20} height={20} fill="var(--content-secondary)" />
          ) : (
            <ChevronDown width={20} height={20} fill="var(--content-secondary)" />
          )}
        </Flex>
      </UnstyledButton>

      <Collapse in={expanded} transitionDuration={200} transitionTimingFunction="ease">
        <Box
          px={24}
          pb={16}
          style={!isLast ? { borderBottom: '1px solid rgba(255, 255, 255, 0.1)' } : undefined}
        >
          <Text
            style={{
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '16px',
              color: 'var(--content-secondary)',
              whiteSpace: 'pre-line',
            }}
          >
            {description}
          </Text>
        </Box>
      </Collapse>

      {!isLast && !expanded && (
        <Box style={{ height: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
      )}
    </Box>
  )
}

export default function DefinitionsBanner() {
  const [opened, { open, close }] = useDisclosure(false)
  const t = useTranslations('marketDataDefinitions')
  const definitions = t.raw('definitions') as Definition[]

  return (
    <>
      <UnstyledButton
        onClick={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px 6px 0',
          borderRadius: 20,
          cursor: 'pointer',
        }}
      >
        <InfoCircle width={16} height={16} fill="#1199FA" />
        <Text variant="body" style={{ color: '#1199FA' }}>
          {t('buttonLabel')}
        </Text>
      </UnstyledButton>

      <ResponsiveModal
        opened={opened}
        onClose={close}
        title={t('modalTitle')}
        size={480}
        radius={40}
        withCloseButton
        fullScreen={false}
        styles={{
          header: { position: 'relative', padding: 24, gap: 12 },
          title: {
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            fontSize: 18,
            fontWeight: 600,
            lineHeight: '32px',
            whiteSpace: 'nowrap',
            color: 'var(--content-primary)',
          },
          content: { minHeight: 680, display: 'flex', flexDirection: 'column' },
          body: { padding: 0, overflowY: 'auto', flex: 1 },
        }}
      >
        <Box>
          {definitions.map((def, i) => (
            <DefinitionRow
              key={def.title}
              title={def.title}
              description={def.description}
              isLast={i === definitions.length - 1}
            />
          ))}
        </Box>
      </ResponsiveModal>
    </>
  )
}
